let cachedKey: CryptoKey | null = null;

/**
 * Derive a per-browser AES key (NOT a secret, just obfuscation-strength).
 * Cached to avoid expensive crypto operations on every API call.
 */
async function getCryptoKey(): Promise<CryptoKey> {
    if (cachedKey) return cachedKey;

    const material = [
        navigator.userAgent,
        navigator.language,
        screen.width,
        screen.height,
    ].join("|");

    const hash = await crypto.subtle.digest(
        "SHA-256",
        new TextEncoder().encode(material)
    );

    cachedKey = await crypto.subtle.importKey(
        "raw",
        hash,
        { name: "AES-GCM" },
        false,
        ["encrypt", "decrypt"]
    );

    return cachedKey;
}


/**
 * Encrypt data for localStorage using AES-GCM.
 */
export async function encrypt(data: unknown): Promise<string> {
    const key = await getCryptoKey();
    const iv = crypto.getRandomValues(new Uint8Array(12)); // 96-bit IV (required)

    const encoded = new TextEncoder().encode(JSON.stringify(data));

    const cipher = await crypto.subtle.encrypt(
        { name: "AES-GCM", iv },
        key,
        encoded
    );

    // Combine IV + cipher text
    const combined = new Uint8Array(iv.length + cipher.byteLength);
    combined.set(iv, 0);
    combined.set(new Uint8Array(cipher), iv.length);

    return btoa(String.fromCharCode(...combined));
}


/**
 * Decrypt data from localStorage.
 */
export async function decrypt(cipherText: string): Promise<any | null> {
    try {
        const key = await getCryptoKey();
        const raw = Uint8Array.from(atob(cipherText), c => c.charCodeAt(0));

        const iv = raw.slice(0, 12);
        const cipher = raw.slice(12);

        const plainBuffer = await crypto.subtle.decrypt(
            { name: "AES-GCM", iv },
            key,
            cipher
        );

        const decoded = new TextDecoder().decode(plainBuffer);
        return JSON.parse(decoded);
    } catch {
        // Tampered or invalid
        return null;
    }
}


